/**
 * FEAT-108 / S-438 — what each role may do, in one place.
 *
 * The API enforces all of it (requireEditor, POST /pieces/:id/decision); the
 * SPA only hides what the role cannot do, so a control never answers 403.
 * Roles are Clerk org roles (ADR-001).
 */

export type OrgRole = "org:admin" | "org:editor" | "org:reviewer" | string;

/** Create, edit, delete and run pieces; write and approve calendar posts. */
export function canEdit(role: OrgRole | null | undefined): boolean {
  return role === "org:admin" || role === "org:editor";
}

/** Approve, Request revision and Reject a piece. */
export function canDecide(role: OrgRole | null | undefined): boolean {
  return canEdit(role) || role === "org:reviewer";
}

/** Stop a run, and the admin-only settings. */
export function isAdminRole(role: OrgRole | null | undefined): boolean {
  return role === "org:admin";
}

/**
 * FEAT-108 / S-437 — which settings screens each role is offered (the spec's
 * access matrix). A screen not listed for a role is neither in the menu nor
 * reachable by URL. The API refuses the same writes on its own.
 */
export type SettingsScreen =
  | "workspaces"
  | "team"
  | "publications"
  | "publish-targets"
  | "voice"
  | "brand"
  | "library"
  | "amplifiers"
  | "social-accounts"
  | "api-keys"
  | "account";

const EVERYONE: readonly SettingsScreen[] = ["social-accounts", "account"];
const EDITOR_UP: readonly SettingsScreen[] = ["publications", "voice", "brand", "library", "amplifiers"];

export function canSeeSettings(screen: SettingsScreen, role: OrgRole | null | undefined): boolean {
  if (isAdminRole(role)) return true;
  if (canEdit(role) && EDITOR_UP.includes(screen)) return true;
  return EVERYONE.includes(screen);
}

/** Where /settings lands: the first screen in the menu the role may open. */
export function defaultSettingsScreen(role: OrgRole | null | undefined): SettingsScreen {
  if (isAdminRole(role)) return "workspaces";
  if (canEdit(role)) return "publications";
  return "social-accounts";
}

/**
 * FEAT-108 / S-437 — the workspaces a settings screen's picker offers. An
 * Admin manages the whole tenant; everyone else sees only the workspaces they
 * are a member of (GET /agencyclients/me), the same list as the desk switcher.
 */
export function visibleWorkspaces<T extends { id: number }>(
  all: readonly T[],
  memberIds: ReadonlySet<number>,
  role: OrgRole | null | undefined
): T[] {
  return isAdminRole(role) ? [...all] : all.filter((w) => memberIds.has(w.id));
}
